import { FC, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import {
  DatePicker,
  DatePickerRef,
  Form,
  ImageUploader,
  ImageUploadItem,
  Input,
  Picker,
  TextArea,
  Toast,
  Dialog,
  Modal
} from 'antd-mobile'
import { CloseOutline } from 'antd-mobile-icons';
import css from './index.module.less'
import { CheckListPopup } from '@/components/CheckListPopup'
import dayjs from 'dayjs'
import { isEmpty } from '@/utils'
import { uploadImage } from '@/api/home'
import { FormContext } from '@/pages/home/AddOrder'
import { isSystemField } from '@/pages/home/AddOrder/utils'
import i18n from '@/utils/i18n';
import { actionTypes, useStoreDispatch, useStoreState } from '@/store'

// 字段类型
export const fieldTypes = new Map([
  ['input', { label: `${i18n.t('order.fieldTypes.input')}`, value: 1 }],
  ['textarea', { label: `${i18n.t('order.fieldTypes.textarea')}`, value: 2 }],
  ['select', { label: `${i18n.t('order.fieldTypes.select')}`, value: 3 }],
  ['multiple-select', { label: `${i18n.t('order.fieldTypes.multiple-select')}`, value: 4 }],
  ['date', { label: `${i18n.t('order.fieldTypes.date')}`, value: 5 }],
  ['upload', { label: `${i18n.t('order.fieldTypes.upload')}`, value: 6 }],
])

export const MODEL_EXT_PREFIX = 'ext'
export const MODEL_COVER_EXT_PREFIX = 'coverExt'

export const DynamicFormItem: FC<{ config: any }> = ({ config }) => {
  const { t } = useTranslation();
  // 如果是预设则直接用fieldKey 否则要放在扩展字段里
  config.name = isSystemField(config) ? config.fieldKey : [MODEL_EXT_PREFIX, config.fieldName]

  switch (config.fieldType) {
    case fieldTypes.get('textarea').value:
      config.placeholder = `${t('common.fill-tips')}`;
      return <TextareaFormItem config={config} />;
    case fieldTypes.get('select').value:
      config.placeholder = `${t('common.select-tips')}`;
      return <SelectFormItem config={config} />;
    case fieldTypes.get('multiple-select').value:
      config.placeholder = `${t('common.select-date-tips')}`;
      config.multiple = true;
      return <SelectFormItem config={config} />;
    case fieldTypes.get('date').value:
      config.placeholder = `${t('common.select-tips')}`;
      return <DateFormItem config={config} />;
    case fieldTypes.get('upload').value:
      config.placeholder = `${t('common.upload-tips')}`;
      return <UploadFormItem config={config} />;
    default:
      config.placeholder = `${t('common.fill-tips')}`;
      return <InputFormItem config={config} />;
  }
}


export const InputFormItem: FC<{ config: any }> = ({ config }) => {
  const { t } = useTranslation();
  return (
    <Form.Item label={config.fieldName} className={css.FormItem} name={config.name} rules={config.reqEnable ? [{ required: true, message: t('common.input-tips') }] : []}>
      <Input
        className={css.Input} maxLength={config.quota} type={config.checkEnable ? 'number' : 'text'}
        clearable
        placeholder={config.placeholder}
      />
    </Form.Item>
  )
}


export const TextareaFormItem: FC<{ config: any }> = ({ config }) => {
  const { t } = useTranslation();
  return (
    <Form.Item label={config.fieldName} className={css.FormItem} name={config.name} rules={config.reqEnable ? [{ required: true, message: t('common.input-tips') }] : []}>
      <TextArea
        className={css.TextArea} rows={5} showCount maxLength={config.quota}
        placeholder={config.placeholder}
      />
    </Form.Item>
  )
}


export const SelectFormItem: FC<{ config: any }> = ({ config }) => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  const renderValue = value => {
    if (isEmpty(value)) return config.placeholder;
    return config.multiple ? value.join(', ') : value;
  }

  return (
    <Form.Item
      label={config.fieldName} className={css.FormItem} name={config.name}
      rules={config.reqEnable ? [{ required: true, message: t('common.input-tips') }, { type: 'array' }] : []}
      onClick={() => setVisible(true)}
    >
      <CheckListPopup
        options={config.keyValues}
        visible={visible} setVisible={setVisible} multiple={config.multiple}
        placeholder={config.placeholder}
      >{renderValue}</CheckListPopup>
    </Form.Item>
  )
}


export const DateFormItem: FC<{ config: any }> = ({ config }) => {
  const form = useContext(FormContext)
  const { t } = useTranslation();

  const onConfirm = value => {
    form.setFieldValue([MODEL_COVER_EXT_PREFIX, config.fieldName], dayjs(value).format('YYYY-MM-DD HH:mm'))
  }

  useEffect(() => {
    onConfirm(config.presetContext)
  }, [])

  return (
    <Form.Item
      label={config.fieldName} className={css.FormItem} name={config.name}
      rules={config.reqEnable ? [{ required: true, message: t('common.input-tips') }, { type: 'date' }] : []}
      trigger='onConfirm'
      onClick={(e, datePickerRef: RefObject<DatePickerRef>) => datePickerRef.current?.open()}
    >
      <DatePicker
        onConfirm={onConfirm}
        precision='minute'
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        {value => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : config.placeholder}
      </DatePicker>
    </Form.Item>
  )
}


export const UploadFormItem: FC<{ config: any }> = ({ config }) => {
  const fileInputRef = useRef<any>(null);
  const { t } = useTranslation();
  const { isFirst }: { isFirst: boolean } = useStoreState();
  const dispatch = useStoreDispatch()
  const maxCount = 9;
  const [fileList, setFileList] = useState<ImageUploadItem[]>([])
  const [visible, setVisible] = useState(false)
  const [showUrl, changeShowUrl] = useState('')

  // 上传前回调
  const beforeUpload = (file: File) => {
    if (file.size > 500 * 1024 * 1024) {
      Toast.show(`${t('order.upload-limit', { size: '500M' })}`)
      return null
    }
    
    // 限制HEIF格式上传
    const forbiddenTypes = ['image/heif', 'image/heic']
    const fileExtension = file.name.toLowerCase().split('.').pop()
    if (forbiddenTypes.includes(file.type) || fileExtension === 'heif' || fileExtension === 'heic') {
      Toast.show(`${t('order.heif-not-supported')}`)
      return null
    }
    
    return file
  }

  // 上传
  const onUpload = async file => {
    const res = await uploadImage({ file })
    if (res?.code === 0) return { url: res.data.url }
  }

  /* 上传条件前置 */
  const onUploadCondition = () => {
    // Dialog.confirm({
    //   content: t('user-center.popText'),
    //   cancelText: <div className={css.cancelBtn}>{t('common.cancel')}</div>,
    //   confirmText: t('common.accept'),
    //   onConfirm: () => {
    //     const inputElement = fileInputRef.current.querySelector('input[type="file"]')
    //     inputElement?.click()

    //     dispatch({ type: actionTypes.SET_FIRST_STRATE, payload: false })
    //   },
    // })

    const inputElement = fileInputRef.current.querySelector('input[type="file"]')
    inputElement?.click()
    dispatch({ type: actionTypes.SET_FIRST_STRATE, payload: false })
  }

  const onDelete = (imageFile) => {
    const { onDelete } = imageFile.props
    onDelete()
  };

  const handleListInfo = (url) => {
    changeShowUrl(url)
    setVisible(true)
  }
  const ShowInfo = () => {
    return <div className={css.showBox}>
      {showUrl.indexOf('mp4') > -1 && <video src={showUrl} width="100%" height="100%" controls></video>}
      {showUrl.indexOf('mp4') < 0 && <img alt={t('common.img-alt')} src={showUrl} width="100%" height="100%" />}
    </div>
  }

  const renderItem = (imageFile, imgObj) => {
    const dom = <div className={css.deleteBtn} onClick={() => onDelete(imageFile)} >
      <CloseOutline className={css.iconPos} />
    </div>

    if (imgObj.url.indexOf('.mp4') > -1) {
      return <div className={css.imgList} key={imgObj.url}>
        {dom}
        <video onClick={() => { handleListInfo(imgObj.url) }} src={imgObj.url} width="100%" height="100%"></video>
      </div>
    }
    return (
      <div className={css.imgList} key={imgObj.url}>
        {dom}
        <img src={imgObj.url} onClick={() => { handleListInfo(imgObj.url) }} alt="" />
      </div>
    );
  }

  return (
    <>
      <div className={css.uploadBox} ref={fileInputRef}>
        {process.env.REACT_APP_IDN_ENV === 'hw' && isFirst && <div className={css.uploadPre} onClick={onUploadCondition}></div>}
        <Form.Item
          label={config.fieldName} className={css.FormItem} name={config.name}
          rules={config.reqEnable ? [{ required: true, message: t('common.input-tips') }] : []}
        >
          <ImageUploader
            // value={fileList}
            accept="image/*,video/mp4"
            onChange={setFileList}
            upload={onUpload}
            multiple
            showFailed={false}
            maxCount={maxCount}
            showUpload={fileList.length < maxCount}
            beforeUpload={beforeUpload}
            onCountExceed={exceed => Toast.show(`${t('order.upload-limit-count', { maxCount, exceed })}`)}
            renderItem={renderItem}
          />
        </Form.Item>
      </div>
      <Modal
        style={{ width: '100%', height: '100%' }}
        visible={visible}
        content={<ShowInfo />}
        showCloseButton={true}
        actions={[]}
        onClose={() => {
          setVisible(false)
          changeShowUrl('')
        }}
      />
    </>
  )
}
